import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Calendar, CalendarChangeParams } from 'primereact/calendar'
import { Card } from 'primereact/card'
import { Dropdown } from 'primereact/dropdown'
import { Fieldset } from 'primereact/fieldset'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JournalDataTable } from './JournalDataTable'
import { MissingJournalsTable } from './MissingJournalsTable'
import { API_LEARNING_JOURNALS_SHORT } from '../../../constants'
import { RequestMethod } from '../../../model/enums/enums'
import { learningJournal } from '../../../model/learnJournal.model'
import { Apprentice, Person } from '../../../model/person.model'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { addLearningJournal } from '../../../redux/official-document/learning-journal/learningJournalActions'
import { AuthRoutes } from '../../../router/routes'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { sortByFirstName } from '../../../util/sortByFirstName'
import { AvatarWithName } from '../../util/AvatarWithName'
import { BackButton } from '../../util/button/BackButton'

dayjs.extend(weekOfYear)

export const JournalDetail = (): JSX.Element => {
  const { loadedBasicInformation, personId } = useAppSelector(
    (state) => state.user
  )
  const journals: learningJournal[] = useAppSelector(
    (state) => state.learningJournals
  )
  const employees = useAppSelector((state) => state.employees)
  const apprentices = useAppSelector((state) => state.apprentices)
  const [currentEmployee, setCurrentEmployee] = useState<Person>()
  const [currentApprentice, setCurrentApprentice] = useState<Apprentice>()
  const [fromDate, setFromDate] = useState<Date>(
    dayjs().subtract(4, 'week').toDate()
  )
  const [toDate, setToDate] = useState<Date>(dayjs().toDate())
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const navigate = useNavigate()

  const dispatcher = useAppDispatch()

  const isApprentice =
    apprentices.find((app) => personId === app.person_id) !== undefined

  const fetchFromDb = async () => {
    setIsLoading(true)
    const result = await handleRequest<learningJournal[]>(
      API_LEARNING_JOURNALS_SHORT(),
      RequestMethod.GET
    )

    if (!result.data || result.error) {
      console.warn('[Journal List] Could fetch the journals, an error ocurred')
      return
    }

    const fetchedJournals = result.data
    fetchedJournals.forEach((journal) =>
      dispatcher(addLearningJournal(journal))
    )
    setIsLoading(false)
  }

  useEffect(() => {
    if (loadedBasicInformation && !isLoading) {
      if (journals.length === 0) {
        fetchFromDb()
      }

      if (isApprentice) {
        const self = apprentices.find((app) => personId === app.person_id)
        setCurrentApprentice(self)
        return
      }

      const self = employees.find((e) => e.person_id === personId)
      setCurrentEmployee(self)
    }
  }, [loadedBasicInformation])

  const filterJournals = (open: boolean) => {
    return journals.filter((journal) => {
      if (
        currentApprentice &&
        currentApprentice.apprentice?.apprentice_id !== journal.id_apprentice
      ) {
        return false
      }
      if (
        currentEmployee &&
        currentEmployee.person_id !== journal.id_responsible
      ) {
        return false
      }
      return (open && !journal.completed) || (!open && journal.completed)
    })
  }

  const personWithAvatar = (p: Person | undefined) => {
    if (!p) {
      return <>{t<string>('core.select')}</>
    }
    return <AvatarWithName person={p}></AvatarWithName>
  }

  return (
    <Card className="pr-2">
      {!isApprentice && (
        <BackButton
          translationKey={'navigation.go_back_overview'}
          route={AuthRoutes.journals}
        />
      )}
      <div className={'p-fluid grid pl-2'}>
        <div className={' col-12 grid mt-1'}>
          <div className={'md:col-6 col-12 mb-0 pr-2'}>
            <label htmlFor={'id_apprentice'}>
              {t<string>('core.apprentice.neutral')}
            </label>
            <Dropdown
              appendTo={document.body}
              options={[...apprentices]
                .sort(sortByFirstName)
                .filter((app) => app.is_active)}
              value={currentApprentice || ''}
              onChange={(e) => setCurrentApprentice(e.value)}
              optionLabel="full_name"
              placeholder={t<string>('core.select')}
              filter
              showClear
              filterBy="full_name"
              valueTemplate={personWithAvatar}
              itemTemplate={personWithAvatar}
              className={'w-full people-dropdown'}
              emptyMessage={t<string>('core.empty_message')}
              disabled={isApprentice}
              emptyFilterMessage={t<string>('core.empty_filter_message')}
            />
          </div>

          <div className={'md:col-6 col-12 mb-0 pr-0'}>
            <label htmlFor={'id_responsible'}>
              {t<string>('core.responsible')}
            </label>
            <Dropdown
              appendTo={document.body}
              options={[...employees].sort(sortByFirstName)}
              value={currentEmployee || ''}
              onChange={(e) => setCurrentEmployee(e.value)}
              optionLabel="full_name"
              placeholder={t<string>('core.select')}
              filter
              showClear
              filterBy="full_name"
              valueTemplate={personWithAvatar}
              itemTemplate={personWithAvatar}
              className={'w-full people-dropdown'}
              emptyMessage={t<string>('core.empty_message')}
              emptyFilterMessage={t<string>('core.empty_filter_message')}
            />
          </div>
        </div>
      </div>
      <div className=" mb-2 w-full">
        <Button
          label={t<string>('journal.create')}
          icon={'fa-solid fa-plus'}
          type={'button'}
          onClick={() =>
            navigate(AuthRoutes.addJournal, {
              state: {
                // this is a hack, not sure why we cannot pass the state object to the route here
                apprentice_id: currentApprentice?.apprentice?.apprentice_id,
              },
            })
          }
          className={'mb-2 p-button-raised'}
        />
      </div>
      <Fieldset
        toggleable
        legend={`${t<string>('journal.open_journals')} (${
          filterJournals(true).length
        })`}
      >
        <JournalDataTable
          data={filterJournals(true)}
          currentApprentice={currentApprentice}
          currentEmployee={currentEmployee}
          concluded={false}
        ></JournalDataTable>
      </Fieldset>

      <Fieldset
        toggleable
        collapsed
        legend={`${t<string>('journal.missing_journals')}`}
        className={'mt-2'}
      >
        <div className="mb-2 justify-content-start flex">
          <div className="mr-2 ">
            <div>
              <label htmlFor={'id_apprentice'}>
                {t<string>('core.date_from')}
              </label>
            </div>
            <Calendar
              className={'min-width-240'}
              value={fromDate}
              dateFormat={'dd.mm.yy'}
              onChange={(e: CalendarChangeParams) =>
                e.value ? setFromDate(e.value) : setFromDate(new Date())
              }
              maxDate={toDate}
              showWeek
            />
          </div>
          <div>
            <div>
              <label htmlFor={'id_apprentice'}>
                {t<string>('core.date_to')}
              </label>
            </div>
            <Calendar
              className={'min-width-240'}
              value={toDate}
              dateFormat={'dd.mm.yy'}
              showWeek
              onChange={(e) =>
                e.value ? setToDate(e.value) : setToDate(new Date())
              }
            />
          </div>
        </div>

        <MissingJournalsTable
          currentApprentice={currentApprentice}
          periodFrom={dayjs(fromDate)}
          periodTo={dayjs(toDate)}
          journals={journals}
        ></MissingJournalsTable>
      </Fieldset>
      <Fieldset
        toggleable
        collapsed
        legend={`${t<string>('journal.closed_journals')} (${
          filterJournals(false).length
        })`}
        className={'mt-2'}
      >
        <JournalDataTable
          data={filterJournals(false)}
          currentApprentice={currentApprentice}
          currentEmployee={currentEmployee}
          concluded={true}
        ></JournalDataTable>
      </Fieldset>
    </Card>
  )
}